import { ASSESSMENT_BASE_PATH } from '../../config/assessmentConfig'
import { trackAssessmentEvent } from '../../lib/analytics/events'
import { applicabilityLabel } from '../../lib/assessment/applicability'
import { computeAssessmentResult } from '../../lib/assessment/engine'
import { saveAssessmentState } from '../../lib/assessment/session'
import type { AssessmentResult, AssessmentState, TopicResult } from '../../lib/assessment/types'
import { blobToBase64, submitAssessmentLead } from '../../lib/lead-capture/adapter'
import { downloadBlob, generateAssessmentPdf } from '../../lib/report/generatePdf'
import { buildReportData } from '../../lib/report/reportData'
import { clear, el, errorBox, fieldLabel, helpText } from './ui'

const SECTOR_LABELS: Record<string, string> = {
  manufacturing: 'Manufacturing',
  construction: 'Construction and engineering',
  real_estate: 'Real estate',
  logistics: 'Logistics',
  oil_gas_energy: 'Oil, gas and energy',
  hospitality: 'Hospitality',
  retail: 'Retail and wholesale',
  healthcare: 'Healthcare',
  financial_services: 'Financial services',
  technology_services: 'Technology and professional services',
  agriculture: 'Agriculture',
  other: 'Other',
}

export function mountResults(root: HTMLElement, state: AssessmentState): void {
  if (!state.completed || !Object.keys(state.materiality).length) {
    clear(root)
    root.append(
      el('div', { className: 'rx-empty' }, [
        el('h2', {}, ['No assessment results yet']),
        el('p', {}, [
          'Complete the assessment to view materiality, carbon readiness and recommended actions.',
        ]),
        el('a', { className: 'btn btn--primary', href: `${ASSESSMENT_BASE_PATH}/start` }, [
          'Start assessment',
        ]),
      ]),
    )
    return
  }

  // Recompute on each view so copy/methodology updates are reflected.
  state.result = computeAssessmentResult(state)
  saveAssessmentState(state)

  trackAssessmentEvent('results_view')
  renderResults(root, state, state.result)
}

function sectionHead(index: string, title: string, subtitle?: string): HTMLElement {
  const head = el('header', { className: 'rx-section__head' })
  head.append(
    el('div', { className: 'rx-section__index' }, [index]),
    el('div', {}, [
      el('h2', { className: 'rx-section__title' }, [title]),
      subtitle ? el('p', { className: 'rx-section__sub' }, [subtitle]) : '',
    ]),
  )
  return head
}

function kpi(label: string, value: string, note: string): HTMLElement {
  return el('div', { className: 'rx-kpi' }, [
    el('div', { className: 'rx-kpi__label' }, [label]),
    el('div', { className: 'rx-kpi__value' }, [value]),
    el('p', { className: 'rx-kpi__note' }, [note]),
  ])
}

function severityClass(severity: string): string {
  if (severity === 'high' || severity === 'critical') return 'rx-tag--high'
  if (severity === 'medium') return 'rx-tag--med'
  return 'rx-tag--low'
}

function renderResults(root: HTMLElement, state: AssessmentState, result: AssessmentResult): void {
  clear(root)
  trackAssessmentEvent('lead_form_view')

  const doc = el('div', { className: 'rx' })
  const profile = result.companyProfile
  const companyName = profile.companyName?.trim() || 'Your company'
  const generated = new Date(result.generatedAt).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })

  // Cover / executive strip
  doc.append(
    el('section', { className: 'rx-cover' }, [
      el('div', { className: 'rx-cover__meta' }, [
        el('span', {}, ['Ecopshub UAE Assessment']),
        el('span', {}, [`Methodology v${result.methodologyVersion}`]),
        el('span', {}, [generated]),
      ]),
      el('h1', { className: 'rx-cover__title' }, [companyName]),
      el('p', { className: 'rx-cover__deck' }, [
        'Preliminary sustainability priorities, carbon inventory readiness and near-term actions based on self-reported inputs.',
      ]),
      el('dl', { className: 'rx-cover__facts' }, [
        el('div', {}, [
          el('dt', {}, ['Sector']),
          el('dd', {}, [SECTOR_LABELS[profile.sector] || profile.sector || 'Not provided']),
        ]),
        el('div', {}, [el('dt', {}, ['Emirate']), el('dd', {}, [profile.emirate || 'Not provided'])]),
        el('div', {}, [
          el('dt', {}, ['Footprint']),
          el('dd', {}, [profile.operatingFootprint || 'Not provided']),
        ]),
        el('div', {}, [
          el('dt', {}, ['Listing']),
          el('dd', {}, [profile.listingStatus || 'Not provided']),
        ]),
      ]),
      el('div', { className: 'rx-cover__actions' }, [
        el('a', { className: 'btn btn--primary', href: '#rx-download' }, ['Get PDF report']),
        el('a', { className: 'btn btn--secondary', href: `${ASSESSMENT_BASE_PATH}/start` }, [
          'Edit assessment',
        ]),
      ]),
    ]),
  )

  // 01 Summary
  const summary = el('section', { className: 'rx-section', id: 'rx-summary' })
  summary.append(
    sectionHead(
      '01',
      'Executive summary',
      'Key readiness indicators from the self-assessment. Not a compliance determination.',
    ),
    el('p', { className: 'rx-prose' }, [result.summary]),
    el('div', { className: 'rx-kpi-row' }, [
      kpi(
        'Overall readiness',
        result.overallReadinessPercent == null ? 'N/A' : `${result.overallReadinessPercent}%`,
        'Blended carbon-process and evidence maturity on applicable answered items.',
      ),
      kpi(
        'Evidence confidence',
        result.evidenceConfidencePercent == null ? 'N/A' : `${result.evidenceConfidencePercent}%`,
        'Self-reported process maturity, not verified audit quality.',
      ),
      kpi(
        'Priority topics',
        String(result.topPriorityTopics.length),
        'Highest-ranked topics from financial and impact materiality.',
      ),
      kpi(
        'Uncertain answers',
        String(result.notSureCount),
        'Explicitly counted; excluded from averages where appropriate.',
      ),
    ]),
    el('p', { className: 'rx-footnote' }, [result.overallReadinessExplanation]),
  )
  doc.append(summary)

  // 02 Materiality
  const matrixSection = el('section', { className: 'rx-section', id: 'rx-matrix' })
  matrixSection.append(
    sectionHead(
      '02',
      'What matters most',
      'Business effect (left to right) versus people & environment effect (bottom to top). Select a point for detail.',
    ),
  )

  const matrixWrap = el('div', { className: 'rx-matrix-layout' })
  const matrix = el('div', { className: 'rx-matrix', role: 'img', 'aria-label': 'Materiality matrix' })
  const plot = el('div', { className: 'rx-matrix__plot' })
  plot.append(
    el('span', { className: 'rx-matrix__quad rx-matrix__quad--tl' }, ['Monitor']),
    el('span', { className: 'rx-matrix__quad rx-matrix__quad--tr' }, ['Strategic']),
    el('span', { className: 'rx-matrix__quad rx-matrix__quad--bl' }, ['Lower priority']),
    el('span', { className: 'rx-matrix__quad rx-matrix__quad--br' }, ['Financial focus']),
  )

  const detail = el('aside', { className: 'rx-matrix__detail', id: 'matrix-detail' }, [
    el('p', { className: 'rx-muted' }, ['Select a topic on the matrix to inspect ratings and evidence gaps.']),
  ])

  const plotted: TopicResult[] = []
  for (const topic of result.topicResults) {
    if (topic.financialScore == null || topic.impactScore == null) continue
    plotted.push(topic)
    const point = el('button', {
      type: 'button',
      className: 'rx-matrix__point',
      title: topic.name,
      'aria-label': `${topic.name}, financial ${topic.financialScore}, impact ${topic.impactScore}`,
    }) as HTMLButtonElement
    const x = ((topic.financialScore - 1) / 4) * 100
    const y = ((topic.impactScore - 1) / 4) * 100
    point.style.left = `${x}%`
    point.style.bottom = `${y}%`
    point.addEventListener('click', () => {
      plot.querySelectorAll('.rx-matrix__point').forEach((n) => n.classList.remove('is-active'))
      point.classList.add('is-active')
      clear(detail)
      detail.append(
        el('div', { className: 'rx-matrix__detail-kicker' }, [
          `Priority #${topic.priorityRank} | ${topic.pillar}`,
        ]),
        el('h3', {}, [topic.name]),
        el('dl', { className: 'rx-dl' }, [
          el('div', {}, [
            el('dt', {}, ['Business effect']),
            el('dd', {}, [`${topic.financialLabel} (${topic.financialScore})`]),
          ]),
          el('div', {}, [
            el('dt', {}, ['People & environment']),
            el('dd', {}, [`${topic.impactLabel} (${topic.impactScore})`]),
          ]),
          el('div', {}, [el('dt', {}, ['Confidence']), el('dd', {}, [topic.confidence])]),
        ]),
        el('p', { className: 'rx-muted' }, [
          `Evidence focus: ${topic.missingEvidence.join('; ') || 'None flagged'}`,
        ]),
        topic.recommendedAction
          ? el('p', { className: 'rx-callout' }, [topic.recommendedAction])
          : '',
      )
    })
    plot.append(point)
  }

  matrix.append(
    el('div', { className: 'rx-matrix__yaxis' }, ['People & environment effect']),
    plot,
    el('div', { className: 'rx-matrix__xaxis' }, ['Business effect →']),
  )
  matrixWrap.append(matrix, detail)
  matrixSection.append(matrixWrap)

  // Priority table
  const table = el('table', { className: 'rx-table' })
  table.append(
    el('thead', {}, [
      el('tr', {}, [
        el('th', {}, ['#']),
        el('th', {}, ['Topic']),
        el('th', {}, ['Pillar']),
        el('th', {}, ['Business effect']),
        el('th', {}, ['People & environment']),
        el('th', {}, ['Confidence']),
      ]),
    ]),
  )
  const tbody = el('tbody')
  for (const topic of result.topPriorityTopics) {
    tbody.append(
      el('tr', {}, [
        el('td', {}, [String(topic.priorityRank)]),
        el('td', { className: 'rx-table__primary' }, [topic.name]),
        el('td', {}, [topic.pillar]),
        el('td', {}, [topic.financialLabel]),
        el('td', {}, [topic.impactLabel]),
        el('td', {}, [topic.confidence]),
      ]),
    )
  }
  table.append(tbody)
  matrixSection.append(
    el('h3', { className: 'rx-subhead' }, ['Highest-priority topics']),
    el('div', { className: 'rx-table-wrap' }, [table]),
    plotted.length
      ? el('p', { className: 'rx-footnote' }, [
          `${plotted.length} topics plotted. “Not sure” / “Not relevant” answers are omitted from the chart. In sustainability reporting, these two ratings are often called financial and impact materiality.`,
        ])
      : el('p', { className: 'rx-footnote' }, ['No topics with complete numeric ratings to plot.']),
  )
  doc.append(matrixSection)

  // 03 Carbon
  const carbon = el('section', { className: 'rx-section', id: 'rx-carbon' })
  carbon.append(
    sectionHead(
      '03',
      'Carbon inventory readiness',
      'Process readiness by GHG scope. Missing data is not interpreted as zero emissions.',
    ),
  )
  const scopeGrid = el('div', { className: 'rx-scope-grid' })
  for (const scope of result.carbonByScope) {
    const card = el('article', { className: 'rx-scope' })
    card.append(
      el('div', { className: 'rx-scope__top' }, [
        el('h3', {}, [`Scope ${scope.scope}`]),
        el('div', { className: 'rx-scope__score' }, [
          scope.scorePercent == null ? 'N/A' : `${scope.scorePercent}%`,
        ]),
      ]),
      el('p', { className: 'rx-muted' }, [
        `${scope.answeredCount}/${scope.applicableCount} applicable sources assessed | ${scope.unansweredCount} unanswered`,
      ]),
    )
    if (scope.gaps.length) {
      const ul = el('ul', { className: 'rx-bullets' })
      for (const g of scope.gaps.slice(0, 4)) ul.append(el('li', {}, [g]))
      card.append(ul)
    } else {
      card.append(el('p', { className: 'rx-muted' }, ['No major gaps flagged for this scope.']))
    }
    scopeGrid.append(card)
  }
  carbon.append(scopeGrid)
  doc.append(carbon)

  // 04 Gaps
  const gaps = el('section', { className: 'rx-section', id: 'rx-gaps' })
  gaps.append(
    sectionHead('04', 'Evidence and process gaps', 'Self-reported gaps requiring attention.'),
  )
  const gapTable = el('table', { className: 'rx-table' })
  gapTable.append(
    el('thead', {}, [
      el('tr', {}, [
        el('th', {}, ['Severity']),
        el('th', {}, ['Area']),
        el('th', {}, ['Finding']),
      ]),
    ]),
  )
  const gapBody = el('tbody')
  for (const gap of result.evidenceGaps.slice(0, 12)) {
    gapBody.append(
      el('tr', {}, [
        el('td', {}, [
          el('span', { className: `rx-tag ${severityClass(gap.severity)}` }, [gap.severity]),
        ]),
        el('td', {}, [gap.area]),
        el('td', {}, [gap.description]),
      ]),
    )
  }
  gapTable.append(gapBody)
  gaps.append(el('div', { className: 'rx-table-wrap' }, [gapTable]))
  doc.append(gaps)

  // 05 Regulatory
  const reg = el('section', { className: 'rx-section', id: 'rx-regulatory' })
  reg.append(
    sectionHead(
      '05',
      'UAE reporting references',
      'Preliminary, source-linked applicability. “No trigger identified” does not prove irrelevance.',
    ),
  )
  const regTable = el('table', { className: 'rx-table' })
  regTable.append(
    el('thead', {}, [
      el('tr', {}, [
        el('th', {}, ['Reference']),
        el('th', {}, ['Status']),
        el('th', {}, ['Notes']),
      ]),
    ]),
  )
  const regBody = el('tbody')
  for (const item of result.applicability) {
    regBody.append(
      el('tr', {}, [
        el('td', {}, [
          el('div', { className: 'rx-table__primary' }, [item.title]),
          el('div', { className: 'rx-muted' }, [
            `${item.authority} | Reviewed ${item.lastReviewed} | `,
            el('a', { href: item.url, target: '_blank', rel: 'noopener noreferrer' }, [
              'Source',
            ]),
          ]),
        ]),
        el('td', {}, [
          el('span', { className: 'rx-tag rx-tag--status' }, [applicabilityLabel(item.state)]),
        ]),
        el('td', {}, [item.notes]),
      ]),
    )
  }
  regTable.append(regBody)
  reg.append(el('div', { className: 'rx-table-wrap' }, [regTable]))
  doc.append(reg)

  // 06 Actions
  const actions = el('section', { className: 'rx-section', id: 'rx-actions' })
  actions.append(
    sectionHead(
      '06',
      '30 to 90 day action plan',
      'Prioritized recommendations generated from your answers.',
    ),
  )
  const actionList = el('ol', { className: 'rx-actions' })
  result.recommendations.forEach((rec, i) => {
    actionList.append(
      el('li', { className: 'rx-action' }, [
        el('div', { className: 'rx-action__meta' }, [
          el('span', { className: 'rx-action__num' }, [String(i + 1).padStart(2, '0')]),
          el('span', { className: `rx-tag ${severityClass(rec.priority)}` }, [rec.priority]),
          el('span', { className: 'rx-muted' }, [`${rec.timeframe} days`]),
        ]),
        el('h3', {}, [rec.action]),
        el('p', {}, [rec.whyRelevant]),
        el('dl', { className: 'rx-dl rx-dl--inline' }, [
          el('div', {}, [el('dt', {}, ['Owner']), el('dd', {}, [rec.owner])]),
          el('div', {}, [
            el('dt', {}, ['Deliverable']),
            el('dd', {}, [rec.expectedDeliverable]),
          ]),
        ]),
      ]),
    )
  })
  actions.append(actionList)
  doc.append(actions)

  // Methodology
  const method = el('section', { className: 'rx-section rx-section--muted' })
  method.append(
    sectionHead(
      '07',
      'How these results were prepared',
      'Plain-language notes on scoring, confidence, and what this report is not.',
    ),
    el('p', { className: 'rx-prose' }, [
      `Assessment method version ${result.methodologyVersion}. All scores are calculated in your browser from the answers you entered. Nothing here is randomly generated or guessed by AI.`,
    ]),
    el('div', { className: 'rx-method-blocks' }, [
      el('article', { className: 'rx-method-block' }, [
        el('h3', {}, ['What “what matters most” means']),
        el('p', {}, [
          'For each topic you answered two questions: how much it could affect the business (money, customers, costs, financing, operations), and how serious the effect could be for people or the environment. Those two ratings are shown separately on the chart. In sustainability reporting, specialists often call this financial and impact materiality. Your answers are a first self-view, not a full stakeholder study.',
        ]),
      ]),
      el('article', { className: 'rx-method-block' }, [
        el('h3', {}, ['How carbon readiness is scored']),
        el('p', {}, [
          'Carbon readiness looks at whether you have the processes and records to build a greenhouse gas inventory later. It does not calculate your emissions in tonnes. Only sources that apply to your business are included. If you said a source does not exist, it is left out of the score. Missing data is never treated as “zero emissions.”',
        ]),
      ]),
      el('article', { className: 'rx-method-block' }, [
        el('h3', {}, ['How “Not sure” and “Not relevant” are handled']),
        el('p', {}, [
          'If you chose Not sure or Not relevant, that answer is not converted into a zero or a fake score. It is counted as uncertain or excluded from averages so gaps stay visible instead of being hidden.',
        ]),
      ]),
      el('article', { className: 'rx-method-block' }, [
        el('h3', {}, ['What evidence confidence means']),
        el('p', {}, [
          'Evidence confidence reflects how complete and organised your data processes appear to be, based on what you reported (documents, owners, reviews, boundaries). Ecopshub has not checked your files. Treat this as self-reported readiness, not an audit result.',
        ]),
      ]),
      el('article', { className: 'rx-method-block' }, [
        el('h3', {}, ['About UAE reporting references']),
        el('p', {}, [
          'Reference statuses are based on the company facts you provided and linked official sources. They are a starting map for discussion. They are not a legal opinion that a law or exchange rule applies, or does not apply, to your entity.',
        ]),
      ]),
      el('article', { className: 'rx-method-block' }, [
        el('h3', {}, ['Important limitations']),
        el('ul', { className: 'rx-bullets' }, [
          el('li', {}, ['This is a preliminary self-assessment for internal planning.']),
          el('li', {}, ['It is not a legal compliance certificate or confirmation that you meet any UAE or exchange requirement.']),
          el('li', {}, ['It is not a verified greenhouse gas inventory and does not replace measurement with real activity data.']),
          el('li', {}, ['It is not an assurance opinion, audit report, or substitute for professional legal, accounting, or sustainability advice.']),
          el('li', {}, ['Results depend on the quality and honesty of the answers entered. Different people in the same company may rate topics differently.']),
        ]),
      ]),
    ]),
    el('p', { className: 'rx-footnote' }, [
      'If you need decisions for disclosure, financing, contracts, or regulatory filings, use these findings as a briefing input and confirm them with qualified advisors and primary source documents.',
    ]),
  )
  doc.append(method)

  // Download / lead
  const cta = el('section', { className: 'rx-section rx-download', id: 'rx-download' })
  cta.append(
    sectionHead('08', 'Download report'),
    el('p', { className: 'rx-prose' }, [
      'Share your contact details and consent to be contacted to generate the branded PDF from the results above.',
    ]),
  )

  const form = el('form', { className: 'rx-form', novalidate: true, id: 'lead-capture' }) as HTMLFormElement
  const status = el('div', { id: 'lead-status' })
  const formGrid = el('div', { className: 'rx-form__grid' })

  const email = el('input', {
    type: 'email',
    id: 'leadEmail',
    className: 'assess-input',
    required: true,
    autocomplete: 'email',
  }) as HTMLInputElement
  const company = el('input', {
    type: 'text',
    id: 'leadCompany',
    className: 'assess-input',
    required: true,
    value: result.companyProfile.companyName || '',
    autocomplete: 'organization',
  }) as HTMLInputElement
  const fullName = el('input', {
    type: 'text',
    id: 'leadName',
    className: 'assess-input',
    required: true,
    autocomplete: 'name',
  }) as HTMLInputElement
  const jobTitle = el('input', {
    type: 'text',
    id: 'leadTitle',
    className: 'assess-input',
  }) as HTMLInputElement
  const phone = el('input', {
    type: 'tel',
    id: 'leadPhone',
    className: 'assess-input',
    autocomplete: 'tel',
  }) as HTMLInputElement

  formGrid.append(
    el('div', {}, [fieldLabel('Work email', true, 'leadEmail'), email]),
    el('div', {}, [fieldLabel('Company name', true, 'leadCompany'), company]),
    el('div', {}, [fieldLabel('Name', true, 'leadName'), fullName]),
    el('div', {}, [fieldLabel('Job title (optional)', false, 'leadTitle'), jobTitle]),
    el('div', {}, [fieldLabel('Phone (optional)', false, 'leadPhone'), phone]),
  )

  const consentReport = el('input', {
    type: 'checkbox',
    className: 'assess-option__input',
    id: 'consentReport',
    required: true,
  }) as HTMLInputElement
  const consentMarketing = el('input', {
    type: 'checkbox',
    className: 'assess-option__input',
    id: 'consentMarketing',
  }) as HTMLInputElement
  const consultant = el('input', {
    type: 'checkbox',
    className: 'assess-option__input',
    id: 'actingAsConsultant',
  }) as HTMLInputElement
  const demo = el('input', {
    type: 'checkbox',
    className: 'assess-option__input',
    id: 'interestInDemo',
  }) as HTMLInputElement

  const checks = el('div', { className: 'assess-option-list' })
  const makeCheck = (input: HTMLInputElement, id: string, text: string) => {
    const label = el('label', { className: 'assess-option assess-option--check', for: id })
    input.addEventListener('change', () => label.classList.toggle('is-selected', input.checked))
    label.append(
      input,
      el('span', { className: 'assess-option__control', 'aria-hidden': 'true' }),
      el('span', { className: 'assess-option__text' }, [text]),
    )
    return label
  }
  checks.append(
    makeCheck(
      consentReport,
      'consentReport',
      'I consent to Ecopshub processing my contact details to deliver the assessment report and related follow-up about this request. *',
    ),
    makeCheck(
      consentMarketing,
      'consentMarketing',
      'Optional: I consent to receiving future marketing communications about Ecopshub.',
    ),
    makeCheck(
      consultant,
      'actingAsConsultant',
      'I am completing this on behalf of a client (consultant / advisor).',
    ),
    makeCheck(
      demo,
      'interestInDemo',
      'I am interested in an Ecopshub carbon accounting demonstration.',
    ),
  )

  const actionsRow = el('div', { className: 'rx-form__actions' })
  const submitBtn = el('button', { type: 'submit', className: 'btn btn--primary' }, [
    'Submit & download PDF',
  ]) as HTMLButtonElement
  const demoCta = el('a', { href: '/contact', className: 'btn btn--secondary', id: 'demo-cta' }, [
    'Book a conversation',
  ]) as HTMLAnchorElement
  demoCta.addEventListener('click', () => trackAssessmentEvent('demo_cta_click'))
  const pitchHost = el('div', { id: 'rx-platform-pitch', className: 'rx-pitch-host' })

  form.addEventListener('submit', async (e) => {
    e.preventDefault()
    clear(status)
    if (!fullName.value.trim()) {
      status.append(errorBox(['Name is required.']))
      return
    }
    if (!consentReport.checked) {
      status.append(errorBox(['Consent to be contacted is required to download the PDF.']))
      return
    }
    trackAssessmentEvent('lead_form_submit')
    submitBtn.disabled = true
    submitBtn.textContent = 'Submitting…'
    try {
      const blob = await generateAssessmentPdf(buildReportData(result))
      const safeName = (result.companyProfile.companyName || company.value.trim() || 'company')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
      const pdfFileName = `ecopshub-uae-assessment-${safeName || 'report'}.pdf`
      const pdfBase64 = await blobToBase64(blob)

      const leadResult = await submitAssessmentLead(
        {
          workEmail: email.value.trim(),
          companyName: company.value.trim(),
          fullName: fullName.value.trim(),
          consentReport: true,
          consentMarketing: consentMarketing.checked,
          jobTitle: jobTitle.value.trim() || undefined,
          phone: phone.value.trim() || undefined,
          interestInDemo: demo.checked,
          actingAsConsultant: consultant.checked,
          qualificationSummary: `${result.leadQualification.scoreBand}: ${result.leadQualification.signals.join('; ')}`,
          methodologyVersion: result.methodologyVersion,
        },
        {
          answers: {
            companyProfile: state.companyProfile,
            responses: state.responses,
            materiality: state.materiality,
            carbon: state.carbon,
            showExtendedTopics: state.showExtendedTopics,
            additionalTopics: state.additionalTopics,
            methodologyVersion: state.methodologyVersion,
          },
          results: result,
          pdfBase64,
          pdfFileName,
        },
      )

      if (!leadResult.ok) {
        trackAssessmentEvent('lead_form_error')
        status.append(errorBox([leadResult.error]))
        return
      }

      trackAssessmentEvent('lead_form_success')
      downloadBlob(blob, pdfFileName)
      trackAssessmentEvent('report_download')
      clear(status)
      status.append(
        el('div', { className: 'assess-success', role: 'status' }, [
          'Details submitted and PDF downloaded. Our team can follow up using the information you provided.',
        ]),
      )
      showPlatformPitch(pitchHost, result)
    } catch {
      trackAssessmentEvent('lead_form_error')
      status.append(
        errorBox(['We could not prepare the PDF or save your details. Try Chrome or Edge, then retry.']),
      )
    } finally {
      submitBtn.disabled = false
      submitBtn.textContent = 'Submit & download PDF'
    }
  })

  actionsRow.append(submitBtn, demoCta)
  form.append(
    formGrid,
    checks,
    helpText(
      'Contact details, your questionnaire answers, results and PDF are stored securely so our team can follow up. Contact details are required to download the PDF.',
    ),
    el('a', { className: 'rx-link', href: `${ASSESSMENT_BASE_PATH}/privacy` }, [
      'Privacy explanation',
    ]),
    actionsRow,
    status,
  )
  cta.append(form)
  doc.append(cta, pitchHost)

  root.append(doc)
}

async function runPdfDownload(result: AssessmentResult, status: HTMLElement): Promise<boolean> {
  try {
    const blob = await generateAssessmentPdf(buildReportData(result))
    const safeName = (result.companyProfile.companyName || 'company')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
    downloadBlob(blob, `ecopshub-uae-assessment-${safeName || 'report'}.pdf`)
    trackAssessmentEvent('report_download')
    return true
  } catch {
    clear(status)
    status.append(
      errorBox(['PDF generation failed in this browser. Try Chrome or Edge, or reload and retry.']),
    )
    return false
  }
}

function showPlatformPitch(host: HTMLElement, result: AssessmentResult): void {
  clear(host)
  trackAssessmentEvent('platform_pitch_view')

  const company = result.companyProfile.companyName?.trim()
  const headline = company
    ? `Turn ${company}'s readiness gaps into an operating system`
    : 'Turn readiness gaps into an operating system'

  const pillars = [
    {
      num: '01',
      title: 'Carbon accounting',
      body: 'Build a defensible Scope 1–3 inventory with activity data, evidence trails and audit-ready roll-ups — the foundation your assessment just stress-tested.',
      href: '/platform#carbon-accounting',
      label: 'Explore carbon accounting',
    },
    {
      num: '02',
      title: 'ESG reporting',
      body: 'Move from scattered spreadsheets to governed disclosures aligned to the frameworks your board, exchange or customers expect.',
      href: '/platform#esg-reporting',
      label: 'Explore ESG reporting',
    },
    {
      num: '03',
      title: 'Decarbonisation',
      body: 'Convert inventory insight into reduction pathways, owners and tracking so the 30–90 day plan becomes a managed programme.',
      href: '/platform#decarbonization',
      label: 'Explore decarbonisation',
    },
  ]

  const card = el('section', {
    className: 'rx-pitch',
    id: 'rx-pitch',
    'aria-label': 'Ecopshub platform next steps',
  })

  const pillarEls = pillars.map((p) => {
    const link = el('a', { className: 'rx-pitch__link', href: p.href }, [p.label]) as HTMLAnchorElement
    link.addEventListener('click', () =>
      trackAssessmentEvent('platform_pitch_click', { target: p.title }),
    )
    return el('article', { className: 'rx-pitch__pillar' }, [
      el('span', { className: 'rx-pitch__num' }, [p.num]),
      el('h3', {}, [p.title]),
      el('p', {}, [p.body]),
      link,
    ])
  })

  const demoBtn = el('a', { className: 'btn btn--primary', href: '/contact' }, [
    'Book a platform walkthrough',
  ]) as HTMLAnchorElement
  demoBtn.addEventListener('click', () =>
    trackAssessmentEvent('platform_pitch_click', { target: 'book_walkthrough' }),
  )

  const platformBtn = el('a', { className: 'btn btn--secondary', href: '/platform' }, [
    'View the platform',
  ]) as HTMLAnchorElement
  platformBtn.addEventListener('click', () =>
    trackAssessmentEvent('platform_pitch_click', { target: 'view_platform' }),
  )

  card.append(
    el('p', { className: 'rx-pitch__eyebrow' }, ['What comes after the assessment']),
    el('h2', { className: 'rx-pitch__title' }, [headline]),
    el('p', { className: 'rx-pitch__lead' }, [
      'Your PDF captures priorities and gaps. Ecopshub is the platform layer that operationalises carbon accounting, ESG reporting and decarbonisation — so evidence, owners and progress live in one place instead of another offline pack.',
    ]),
    el('div', { className: 'rx-pitch__grid' }, pillarEls),
    el('div', { className: 'rx-pitch__cta' }, [demoBtn, platformBtn]),
  )

  host.append(card)
  card.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

export function mountReport(root: HTMLElement, state: AssessmentState): void {
  if (!state.result && state.completed) {
    state.result = computeAssessmentResult(state)
    saveAssessmentState(state)
  }
  if (!state.result) {
    mountResults(root, state)
    return
  }
  clear(root)
  root.append(
    el('div', { className: 'rx-empty' }, [
      el('h2', {}, ['Report preview']),
      el('p', {}, [
        'Open the results page to review findings and download the branded PDF.',
      ]),
      el('a', { className: 'btn btn--primary', href: `${ASSESSMENT_BASE_PATH}/results#rx-download` }, [
        'Go to results',
      ]),
    ]),
  )
}
